/**
 * Where the acting-partner selection is persisted, and how to purge it.
 *
 * A module of its own, with NO imports, for one reason: both
 * `ActingPartnerContext` (which reads and writes the selection) and
 * `AuthContext` (which must drop it at sign-out) need this, and
 * `ActingPartnerContext` already depends on `useAuth`. Putting the purge on the
 * context would make that a cycle — AuthContext → ActingPartnerContext →
 * useAuth → AuthContext. It would happen to work, because the only call is at
 * runtime rather than module-eval, but a cycle that survives on evaluation
 * order is not something to leave in the auth path.
 *
 * sessionStorage, and keyed per user, for the reasons in ActingPartnerContext:
 * scoped to the tab, and a second operator signing in on the same machine must
 * not inherit the first one's choice.
 */

const ACTING_PARTNER_KEY_PREFIX = 'zenvoya.crm.actingPartner.';

export const actingPartnerKeyFor = (userId) =>
  `${ACTING_PARTNER_KEY_PREFIX}${userId || 'anonymous'}`;

/**
 * Drop EVERY stored acting-partner selection in this tab.
 *
 * Called from the portal's sign-out. Sign-out unmounts the provider, so nothing
 * in it ran and the key simply stayed — a logout cleared `_zenvoya_crm_session`
 * and left `zenvoya.crm.actingPartner.<userId>` sitting next to it with the
 * whole committed selection, `travellerUserId` included. Confirmed against the
 * deployed console: the key survives a logout intact.
 *
 * Two reasons that has to stop, and the first is the one that matters:
 *
 *  - SAFETY. On the next sign-in the selection is restored, so the chooser is
 *    skipped and the console comes up already acting for a partner nobody chose
 *    this session — silently, with prices and bookings scoped to them. That is
 *    precisely the failure ActingPartnerContext's own doc comment says the
 *    feature exists to prevent, and the per-user key does not prevent it: the
 *    same operator signing back in restores it, and it is the same operator
 *    almost every time.
 *  - CORRECTNESS. The restored `travellerUserId` was provisioned under the
 *    PREVIOUS session. Bringing the frame up against it with a freshly issued
 *    portal token skips the step that would have re-resolved it
 *    (startActingPartnerSession), so anything stale about it surfaces as a
 *    frame with no usable session and no chooser on screen to correct it.
 *
 * ALL keys, not just the signed-out user's: at logout the id is not reliably
 * readable any more, and a tab must not keep a selection belonging to whoever
 * used it before. Prefix-scoped, so nothing else in sessionStorage is touched.
 */
export const purgeActingPartnerStorage = () => {
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(ACTING_PARTNER_KEY_PREFIX))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    /* private mode — nothing was persisted to begin with */
  }
};
